import React, { useEffect, useMemo, useState } from "react";
import { sortStaffAssignedFirst } from "./qmsPublishStaffUtils";

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

const STAFF_FILTERS = [
  { key: "all", label: "All" },
  { key: "assigned", label: "Assigned" },
  { key: "available", label: "Not assigned" },
];

function QmsDocumentLibraryStaffPanel({
  active = true,
  staff = [],
  loading = false,
  savingStaffIds = [],
  canManageStaff = false,
  embedded = false,
  hideFilters = false,
  assignedOnly = false,
  sortAssignedFirst = false,
  onToggleStaff,
  onBulkToggleStaff,
}) {
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const savingSet = useMemo(
    () => new Set((savingStaffIds || []).map(String)),
    [savingStaffIds],
  );
  const isBulkSaving = savingSet.size > 1;

  useEffect(() => {
    if (!active) return;
    setSearch("");
    setActiveFilter("all");
    setSelectedIds(new Set());
  }, [active]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [search, activeFilter]);

  const sourceStaff = useMemo(() => {
    if (!assignedOnly) return staff;
    return staff.filter((row) => Number(row.Assigned) === 1);
  }, [staff, assignedOnly]);

  const effectiveHideFilters = hideFilters || assignedOnly;

  const assignedCount = useMemo(
    () => sourceStaff.filter((row) => Number(row.Assigned) === 1).length,
    [sourceStaff],
  );

  const filteredStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = sourceStaff.filter((row) => {
      const isAssigned = Number(row.Assigned) === 1;
      if (!effectiveHideFilters) {
        if (activeFilter === "assigned" && !isAssigned) return false;
        if (activeFilter === "available" && isAssigned) return false;
      }
      if (!q) return true;
      return (
        String(row.StaffName || "")
          .toLowerCase()
          .includes(q) ||
        String(row.Roles || "")
          .toLowerCase()
          .includes(q)
      );
    });

    return sortAssignedFirst ? sortStaffAssignedFirst(rows) : rows;
  }, [sourceStaff, search, activeFilter, effectiveHideFilters, sortAssignedFirst]);

  const filteredIds = useMemo(
    () => filteredStaff.map((row) => String(row.StaffId)),
    [filteredStaff],
  );

  const allVisibleSelected =
    filteredIds.length > 0 &&
    filteredIds.every((id) => selectedIds.has(id));

  const selectedRows = useMemo(
    () => filteredStaff.filter((row) => selectedIds.has(String(row.StaffId))),
    [filteredStaff, selectedIds],
  );

  const grantableSelected = selectedRows.filter(
    (row) => Number(row.Assigned) !== 1,
  );
  const removableSelected = selectedRows.filter(
    (row) => Number(row.Assigned) === 1,
  );

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(filteredIds));
  };

  const toggleSelect = (staffId) => {
    const id = String(staffId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkGrant = () => {
    if (!grantableSelected.length || isBulkSaving) return;
    onBulkToggleStaff?.(grantableSelected, true);
    clearSelection();
  };

  const handleBulkRemove = () => {
    if (!removableSelected.length || isBulkSaving) return;
    onBulkToggleStaff?.(removableSelected, false);
    clearSelection();
  };

  const hasSelection = selectedRows.length > 0;
  const rootClassName = embedded
    ? "qms-doc-lib__staff-embedded"
    : "qms-doc-workspace__panel qms-doc-workspace__panel--staff";
  const layoutClassName = `qms-doc-lib__staff-layout${
    embedded ? " is-publish" : ""
  }`;

  return (
    <div className={rootClassName}>
      <div className={layoutClassName}>
        <div className="qms-doc-lib__staff-main">
          {embedded ? (
            <div className="qms-doc-lib__staff-inline-toolbar">
              <label className="qms-doc-lib__staff-search qms-doc-lib__staff-search--inline">
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  type="search"
                  placeholder="Name or role…"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </label>
              <div className="qms-doc-lib__staff-inline-stats">
                <span className="is-assigned">{assignedCount} assigned</span>
                <span>{sourceStaff.length} total</span>
              </div>
            </div>
          ) : null}

          {canManageStaff && filteredStaff.length > 0 && !loading ? (
            <div
              className={`qms-doc-lib__staff-bulk-bar${
                hasSelection ? " is-active" : ""
              }`}
              data-tour="qms-doc-workspace-staff-bulk"
              role={hasSelection ? "region" : undefined}
              aria-label={hasSelection ? "Bulk staff actions" : undefined}
            >
              {!hasSelection ? (
                <>
                  <label className="qms-doc-lib__staff-select-all">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      disabled={isBulkSaving}
                    />
                    <span>Select all visible</span>
                  </label>
                  <p className="qms-doc-lib__staff-bulk-idle-hint">
                    Tick staff cards to update access in bulk.
                  </p>
                </>
              ) : (
                <>
                  <label className="qms-doc-lib__staff-select-all">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      disabled={isBulkSaving}
                    />
                    <span>Select all visible</span>
                  </label>

                  <div className="qms-doc-lib__staff-bulk-summary">
                    <span className="qms-doc-lib__staff-bulk-count-badge">
                      {selectedRows.length} selected
                    </span>
                    <span className="qms-doc-lib__staff-bulk-meta">
                      {grantableSelected.length > 0 &&
                      removableSelected.length > 0 ? (
                        <>
                          {grantableSelected.length} to grant
                          <span aria-hidden="true"> · </span>
                          {removableSelected.length} to remove
                        </>
                      ) : grantableSelected.length > 0 ? (
                        `${grantableSelected.length} ready to grant access`
                      ) : removableSelected.length > 0 ? (
                        `${removableSelected.length} ready to remove access`
                      ) : (
                        "No changes available"
                      )}
                    </span>
                  </div>

                  <div className="qms-doc-lib__staff-bulk-actions">
                    {grantableSelected.length > 0 ? (
                      <button
                        type="button"
                        className="qms-doc-lib__staff-bulk-btn is-grant"
                        disabled={isBulkSaving || loading}
                        onClick={handleBulkGrant}
                      >
                        <i className="fas fa-user-plus" aria-hidden="true" />
                        Grant ({grantableSelected.length})
                      </button>
                    ) : null}
                    {removableSelected.length > 0 ? (
                      <button
                        type="button"
                        className="qms-doc-lib__staff-bulk-btn is-remove"
                        disabled={isBulkSaving || loading}
                        onClick={handleBulkRemove}
                      >
                        <i className="fas fa-user-minus" aria-hidden="true" />
                        Remove ({removableSelected.length})
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="qms-doc-lib__staff-bulk-btn is-clear"
                      disabled={isBulkSaving}
                      onClick={clearSelection}
                      aria-label="Clear selection"
                    >
                      <i className="fas fa-times" aria-hidden="true" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : null}

          <div className="qms-doc-lib__staff-scroll">
            {loading ? (
              <div className="qms-doc-lib__staff-empty">
                <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                <span>Loading staff…</span>
              </div>
            ) : filteredStaff.length === 0 ? (
              <div className="qms-doc-lib__staff-empty">
                <i className="fas fa-user-slash" aria-hidden="true" />
                <span>No staff match your search.</span>
              </div>
            ) : (
              <div className="qms-doc-lib__staff-grid" data-tour="qms-doc-workspace-staff-grid">
                {filteredStaff.map((row) => {
                  const isAssigned = Number(row.Assigned) === 1;
                  const isSaving = savingSet.has(String(row.StaffId));
                  const isSelected = selectedIds.has(String(row.StaffId));
                  return (
                    <div
                      key={row.StaffId}
                      className={`qms-doc-lib__staff-card${
                        isAssigned ? " is-assigned" : ""
                      }${isSaving ? " is-saving" : ""}${
                        isSelected ? " is-selected" : ""
                      }`}
                    >
                      {canManageStaff ? (
                        <label className="qms-doc-lib__staff-card-check">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isBulkSaving || isSaving}
                            onChange={() => toggleSelect(row.StaffId)}
                          />
                          <span className="visually-hidden">
                            Select {row.StaffName}
                          </span>
                        </label>
                      ) : null}

                      <div className="qms-doc-lib__staff-card-body">
                        <div
                          className="qms-doc-lib__staff-card-avatar"
                          aria-hidden="true"
                        >
                          {getInitials(row.StaffName)}
                        </div>
                        <div className="qms-doc-lib__staff-card-main">
                          <div
                            className="qms-doc-lib__staff-card-name"
                            title={row.StaffName}
                          >
                            {row.StaffName}
                          </div>
                          <div
                            className="qms-doc-lib__staff-card-role"
                            title={row.Roles || ""}
                          >
                            {row.Roles || "—"}
                          </div>
                        </div>
                        <div className="qms-doc-lib__staff-card-action">
                          {canManageStaff ? (
                            <button
                              type="button"
                              className={`qms-doc-lib__staff-toggle${
                                isAssigned ? " is-remove" : " is-add"
                              }`}
                              disabled={isSaving || loading || isBulkSaving}
                              onClick={() => onToggleStaff?.(row, !isAssigned)}
                            >
                              {isSaving ? (
                                <>
                                  <i
                                    className="fas fa-spinner fa-spin"
                                    aria-hidden="true"
                                  />
                                  Saving…
                                </>
                              ) : isAssigned ? (
                                <>
                                  <i
                                    className="fas fa-user-minus"
                                    aria-hidden="true"
                                  />
                                  Remove access
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-user-plus"
                                    aria-hidden="true"
                                  />
                                  Grant access
                                </>
                              )}
                            </button>
                          ) : (
                            <span
                              className={`qms-doc-lib__staff-status${
                                isAssigned ? " is-assigned" : ""
                              }`}
                            >
                              {isAssigned ? "Has access" : "No access"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {!embedded ? (
        <aside
          className="qms-doc-lib__staff-rail"
          aria-label="Staff filters"
          data-tour="qms-doc-workspace-staff-rail"
        >
          <div className="qms-doc-lib__staff-rail-block">
            <div className="qms-doc-lib__staff-rail-label">Search</div>
            <label className="qms-doc-lib__staff-search qms-doc-lib__staff-search--rail">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="search"
                placeholder="Name or role…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
          </div>

          {!effectiveHideFilters ? (
          <div className="qms-doc-lib__staff-rail-block">
            <div className="qms-doc-lib__staff-rail-label">Filter</div>
            <div className="qms-doc-lib__staff-rail-filters" role="tablist">
              {STAFF_FILTERS.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  role="tab"
                  aria-selected={activeFilter === filter.key}
                  className={`qms-doc-lib__staff-filter qms-doc-lib__staff-filter--rail${
                    activeFilter === filter.key ? " is-active" : ""
                  }`}
                  onClick={() => setActiveFilter(filter.key)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
          ) : null}

          <div className="qms-doc-lib__staff-rail-block">
            <div className="qms-doc-lib__staff-rail-label">Summary</div>
            <div className="qms-doc-lib__staff-rail-stats">
              <div className="qms-doc-lib__staff-rail-stat is-assigned">
                <span className="qms-doc-lib__staff-rail-stat-value">
                  {assignedCount}
                </span>
                <span className="qms-doc-lib__staff-rail-stat-label">
                  Assigned
                </span>
              </div>
              <div className="qms-doc-lib__staff-rail-stat">
                <span className="qms-doc-lib__staff-rail-stat-value">
                  {sourceStaff.length}
                </span>
                <span className="qms-doc-lib__staff-rail-stat-label">Total</span>
              </div>
            </div>
          </div>
        </aside>
        ) : null}
      </div>
    </div>
  );
}

export default QmsDocumentLibraryStaffPanel;
